import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
  IconShieldCheck,
  IconRocket,
  IconClipboardCheck,
  IconCarCrash,
  IconDroplet,
  IconCertificate,
  IconBrandWindows,
  IconArrowRight,
} from '@tabler/icons-react';

import slide1Img from '../../assets/signup-slides/slide-1-onboarding.jpg';
import slide2Img from '../../assets/signup-slides/slide-2-checks.jpg';
import slide3Img from '../../assets/signup-slides/slide-3-incidents.jpg';
import slide4Img from '../../assets/signup-slides/slide-4-fuel.jpg';
import slide5Img from '../../assets/signup-slides/slide-5-licences.jpg';

const slides = [
  {
    icon: IconRocket,
    title: 'Up and running in under 10 minutes.',
    image: slide1Img,
    body: 'Set up your organisation, add your branches and invite your team. No IT department needed.',
    stats: [{ val: '<10', lbl: 'Min to go live' }, { val: 'UK', lbl: 'GDPR compliant' }],
  },
  {
    icon: IconClipboardCheck,
    title: 'Every check. Every day. Every branch.',
    image: slide2Img,
    body: 'Operators complete their walkaround in under a minute. Every result logged, timestamped and tied to a named driver.',
    stats: [{ val: '96', lbl: 'Checks today' }, { val: '4', lbl: 'Branches' }],
  },
  {
    icon: IconCarCrash,
    title: 'Incidents logged the moment they happen.',
    image: slide3Img,
    body: 'Crash detection arms automatically after every vehicle check. Manager notified in seconds.',
    stats: [{ val: '<30s', lbl: 'Alert time' }, { val: '100%', lbl: 'Logged' }],
  },
  {
    icon: IconDroplet,
    title: 'Fuel accounted for. Orders automatic.',
    image: slide4Img,
    body: 'Tank levels tracked in real time. Order emails fire automatically when stocks run low.',
    stats: [{ val: 'Auto', lbl: 'Ordering' }, { val: 'Live', lbl: 'Tank levels' }],
  },
  {
    icon: IconCertificate,
    title: 'MOT, tax and licences — always current.',
    image: slide5Img,
    body: 'Vehicle data pulls from the DVLA automatically. Reminders fire before anything expires.',
    stats: [{ val: 'DVLA', lbl: 'Connected' }, { val: 'Auto', lbl: 'Reminders' }],
  },
];

export default function Signup() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  // Slideshow timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Generate slug from org name
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      const slug = generateSlug(orgName);
      const orgId = slug + '-' + Date.now().toString(36);

      // Create organisation in Firestore
      await setDoc(doc(db, 'organisations', orgId), {
        name: orgName,
        slug,
        website,
        plan: 'pending',
        brandColour: '#E8610A',
        accentColour: null,
        bgColour: null,
        logo: null,
        industry: null,
        dataRetentionYears: 3,
        createdAt: serverTimestamp(),
        features: {
          crashDetection: false,
          groupOverview: false,
          dvlaIntegration: false,
          licenceVerification: false,
          pdfReports: false,
          complianceScoring: false,
          anomalyDetection: false,
          maxBranches: 1,
          maxUsers: 10,
        }
      });

      // Create user profile
      await setDoc(doc(db, 'organisations', orgId, 'users', uid), {
        name: `${firstName} ${lastName}`,
        email,
        role: 'senior_manager',
        orgId,
        createdAt: serverTimestamp(),
      });

      // Navigate to brand scan with org info
      navigate('/signup/scan', {
        state: { orgId, orgName, website, slug }
      });

    } catch (err) {
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 6 characters');
          break;
        default:
          setError('Something went wrong — please try again');
      }
      setLoading(false);
    }
  };

  const slide = slides[currentSlide];
  const SlideIcon = slide.icon;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel — 40% */}
      <div style={{
        width: '40%',
        background: slide.image
          ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${slide.image}) center/cover`
          : 'var(--color-brand)',
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
      }}>

        {/* Brand mark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>Veritas</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        {/* Slide */}
        <div style={{ flex: 1 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <SlideIcon size={26} color="white" stroke={1.5} />
          </div>
          <div style={{
            fontSize: 26,
            fontWeight: 400,
            color: '#fff',
            lineHeight: 1.35,
            marginBottom: 14,
            maxWidth: 400,
          }}>
            {slide.title}
          </div>
          <div style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.72)',
            lineHeight: 1.7,
            marginBottom: 28,
            maxWidth: 400,
          }}>
            {slide.body}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {slide.stats.map((stat, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 18px',
              }}>
                <div style={{ fontSize: 22, fontWeight: 400, color: '#fff' }}>{stat.val}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{stat.lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 5, marginTop: 28 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrentSlide(i)}
              style={{
                width: i === currentSlide ? 16 : 5,
                height: 5,
                borderRadius: 3,
                background: i === currentSlide ? '#fff' : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                transition: 'width 0.3s, background 0.3s',
              }}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 20,
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
            An <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
            &copy; 2026 Agriware Ltd
          </div>
        </div>
      </div>

      {/* Right panel — 60% */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
        overflow: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 6 }}>Get started</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 24 }}>
            Create your Veritas account and get your fleet compliant from day one.
          </p>

          {/* Microsoft SSO */}
          <button style={{
            width: '100%',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            border: '0.5px solid var(--color-border-medium)',
            borderRadius: 'var(--radius-md)',
            padding: '11px',
            fontSize: 13,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 16,
          }}>
            <div style={{
              width: 16,
              height: 16,
              background: '#0078D4',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <IconBrandWindows size={10} color="white" stroke={1.5} />
            </div>
            Continue with Microsoft 365
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>or sign up with email</span>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
          </div>

          <form onSubmit={handleSignup}>

            {/* Name row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  First name
                </label>
                <input
                  type="text"
                  placeholder="Dan"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  required
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Last name
                </label>
                <input
                  type="text"
                  placeholder="Smith"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Organisation name
              </label>
              <input
                type="text"
                placeholder="George Browns Ltd"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                required
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
              {orgName && (
                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                  Your login URL: <span style={{ color: 'var(--color-brand)' }}>{generateSlug(orgName)}</span>.veritas.agriware.uk
                </div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Website
              </label>
              <input
                type="text"
                placeholder="georgebrowns.co.uk"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                We'll use this to pull your logo and brand colours automatically
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Work email
              </label>
              <input
                type="email"
                placeholder="dan@georgebrowns.co.uk"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                Password
              </label>
              <input
                type="password"
                placeholder="Min. 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            {error && (
              <div style={{
                fontSize: 12,
                color: 'var(--color-danger)',
                background: 'var(--color-danger-light)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                marginBottom: 14,
              }}>
                {error}
              </div>
            )}

            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'center', marginBottom: 14, lineHeight: 1.5 }}>
              By creating an account you agree to Veritas'{' '}
              <span style={{ color: 'var(--color-brand)', cursor: 'pointer' }}>Terms of Service</span>
              {' '}and{' '}
              <span style={{ color: 'var(--color-brand)', cursor: 'pointer' }}>Privacy Policy</span>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: 'var(--color-brand)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: '12px',
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginBottom: 14,
              }}
            >
              {loading ? 'Creating account...' : 'Create account'}
              {!loading && <IconArrowRight size={16} stroke={1.5} />}
            </button>
          </form>

          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'center', marginBottom: 14 }}>
            Already have an account?{' '}
            <span
              onClick={() => navigate('/login')}
              style={{ color: 'var(--color-brand)', cursor: 'pointer' }}
            >
              Sign in
            </span>
          </div>

          <div style={{
            fontSize: 11,
            color: 'var(--color-text-tertiary)',
            textAlign: 'center',
            paddingTop: 16,
            borderTop: '0.5px solid var(--color-border-light)',
          }}>
            Veritas is a product by{' '}
            <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>Agriware</span>
          </div>

        </div>
      </div>
    </div>
  );
}